import { formatDateToHHmmDDMMYYYY, handleDownload } from '@/utils/common';
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined,
    PaperClipOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Col, Row, Select, Table, TableColumnsType, Tabs, Typography, Button as ButtonAnt, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { setMessages } from '@/redux/slices/app.slice';
import { Form } from 'antd';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import {
    getHistory,
    getListAgeComboFood,
    getListNutritionHealthStatusComboFood,
    getListNutritionStatusZScoreComboFood,
    getListNutritionStatusComboFood,
    getListNutritionStatus60to19ComboFood,
    watchListAgeComboFood,
    watchListNutritionHealthStatusComboFood,
    watchListNutritionStatusZScoreComboFood,
    watchListNutritionStatusComboFood,
    watchListNutritionStatus60to19ComboFood,
    importFile,
    watchListHistory,
    watchPaginationHistory,
    watchComboFoodLoading,
    watchComboFoodMessage,
    resetMessages,
    downloadTemplateComboFood
} from '@/redux/slices/combo-food.slice';
import { AGE_GROUPS } from '@/utils/nutrition-columns.utils';

type AgeID = typeof AGE_GROUPS.UNDER_60_MONTHS[number] |
    typeof AGE_GROUPS.FROM_60_MONTHS_TO_19_YEARS[number] |
    typeof AGE_GROUPS.OVER_19_YEARS[number];

type PageChangeParams = {
    page?: number;
    pageSize?: number;
};

export default function ComboFoodByListTable() {
    const [activeTab, setActiveTab] = useState<string>('INPUT');
    const dispatch = useAppDispatch();
    const pagination = useAppSelector(watchPaginationHistory);
    const listHistory = useAppSelector(watchListHistory);
    const loading = useAppSelector(watchComboFoodLoading);
    const messageInfo = useAppSelector(watchComboFoodMessage);
    const listAge = useAppSelector(watchListAgeComboFood);
    const listNutritionHealthStatusComboFood = useAppSelector(watchListNutritionHealthStatusComboFood);
    const listNutritionStatusZScoreComboFood = useAppSelector(watchListNutritionStatusZScoreComboFood);
    const listNutritionStatusComboFood = useAppSelector(watchListNutritionStatusComboFood);
    const listNutritionStatus60to19ComboFood = useAppSelector(watchListNutritionStatus60to19ComboFood);

    const [fileName, setFileName] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [age, setAge] = useState<string>();
    const [healthCondition, setHealthCondition] = useState<string>();
    const [zScore_60, setZSCORE_60] = useState<string>();
    const [bmi60Month19Age, setBMI60Month19Age] = useState<string>();
    const [bmiGe19Age, setBMIGe19Age] = useState<string>();
    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const [isUnder19, setIsUnder19] = useState<boolean>(false);
    const [disableZScore, setDisableZScore] = useState<boolean>(true);
    const [disableBMI60_19, setDisableBMI60_19] = useState<boolean>(true);
    const [disableBMIGe19, setDisableBMIGe19] = useState<boolean>(true);

    const [form] = Form.useForm();
    const { push } = useQuery();

    const checkFormValid = useCallback(() => {
        const touched = form.isFieldsTouched(true);
        const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
        const hasFile = !!selectedFile;

        setIsFormValid(!touched && !hasErrors && hasFile);
    }, [form, selectedFile]);

    const handleDownLoadTemplateComboFood = async () => {
        const template = await dispatch(downloadTemplateComboFood()).unwrap();
        if (template) {
            handleDownload(template.URL, template.NAME);
        }
    }

    const handleAgeChange = useCallback((value: AgeID | undefined) => {
        setAge(value);

        if (value) {
            const isUnder60Months = AGE_GROUPS.UNDER_60_MONTHS.includes(value as typeof AGE_GROUPS.UNDER_60_MONTHS[number]);
            const isFrom60MonthsTo19Years = AGE_GROUPS.FROM_60_MONTHS_TO_19_YEARS.includes(value as typeof AGE_GROUPS.FROM_60_MONTHS_TO_19_YEARS[number]);
            const isOver19Years = AGE_GROUPS.OVER_19_YEARS.includes(value as typeof AGE_GROUPS.OVER_19_YEARS[number]);

            setIsUnder19(!isOver19Years);

            if (isUnder60Months) {
                setDisableZScore(false);
                setDisableBMI60_19(true);
                setDisableBMIGe19(true);

                form.setFieldsValue({
                    bmi60Month19Age: undefined,
                    bmiGe19Age: undefined
                });
                setBMI60Month19Age(undefined);
                setBMIGe19Age(undefined);
            } else if (isFrom60MonthsTo19Years) {
                setDisableZScore(true);
                setDisableBMI60_19(false);
                setDisableBMIGe19(true);

                form.setFieldsValue({
                    zScore_60: undefined,
                    bmiGe19Age: undefined
                });
                setZSCORE_60(undefined);
                setBMIGe19Age(undefined);
            } else if (isOver19Years) {
                setDisableZScore(true);
                setDisableBMI60_19(true);
                setDisableBMIGe19(false);

                form.setFieldsValue({
                    zScore_60: undefined,
                    bmi60Month19Age: undefined
                });
                setZSCORE_60(undefined);
                setBMI60Month19Age(undefined);
            }
        } else {
            setIsUnder19(false);
            setDisableZScore(true);
            setDisableBMI60_19(true);
            setDisableBMIGe19(true);

            form.setFieldsValue({
                zScore_60: undefined,
                bmi60Month19Age: undefined,
                bmiGe19Age: undefined
            });
            setZSCORE_60(undefined);
            setBMI60Month19Age(undefined);
            setBMIGe19Age(undefined);
        }

        form.setFieldsValue({ healthCondition: undefined });
        setHealthCondition(undefined);
    }, [form]);

    useEffect(() => {
        checkFormValid();
    }, [checkFormValid]);

    useEffect(() => {
        dispatch(getHistory({}));
        dispatch(getListAgeComboFood('FOOD_COMBO_IMPORT'));
        dispatch(getListNutritionHealthStatusComboFood());
        dispatch(getListNutritionStatusComboFood());
        dispatch(getListNutritionStatusZScoreComboFood());
        dispatch(getListNutritionStatus60to19ComboFood());
    }, [dispatch]);

    const listAgeOptions = listAge.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionHealthStatusOptions = listNutritionHealthStatusComboFood
        .filter(item => {
            return isUnder19 ? item.displayName === "Bình thường" : true;
        })
        .map((item) => ({
            value: item.healthStatusId,
            label: item.displayName
        }));

    const listNutritionStatusZScoreOptions = listNutritionStatusZScoreComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionStatusOptions = listNutritionStatusComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionStatus60to19Options = listNutritionStatus60to19ComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel.sheet.macroEnabled.12',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
                'application/vnd.ms-excel.template.macroEnabled.12',
                'application/vnd.ms-excel.sheet.binary.macroEnabled.12'
            ];
            const validExtensions = ['.xls', '.xlsx', '.xlsm', '.xltx', '.xltm', '.xlsb'];


            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

            if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
                message.error('Vui lòng chọn file Excel hợp lệ (.xls hoặc .xlsx)');
                return;
            }

            setFileName(file.name);
            setSelectedFile(file);
        }
    };

    const onFinish = async () => {
        if (!selectedFile) {
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('AGE_RANGE_ID', age ?? '');
        const healthStatusIds = healthCondition
            ? Array.isArray(healthCondition)
                ? healthCondition
                : [healthCondition]
            : [];

        formData.append('HEALTH_STATUS_IDS', JSON.stringify(healthStatusIds));

        if (zScore_60) {
            formData.append('ZSCORE_60', zScore_60);
        }
        if (bmi60Month19Age) {
            formData.append('BMI_60_MONTH_19_AGE', bmi60Month19Age);
        }
        if (bmiGe19Age) {
            formData.append('BMI_GE_19_AGE', bmiGe19Age);
        }

        await dispatch(importFile(formData));
        setSelectedFile(null);
        setFileName(null);
        form.resetFields();

        setAge(undefined);
        setHealthCondition(undefined);
        setZSCORE_60(undefined);
        setBMI60Month19Age(undefined);
        setBMIGe19Age(undefined);

        push(PageURLs.ofComboFoodManagement())

    };

    const handleCancel = () => {
        setFileName(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );

            if (messageInfo.type === 'success') {
                handleCancel();
            }
            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo]);

    const tabItems = [
        {
            key: 'INPUT',
            label: 'Nhập dữ liệu'
        },
        {
            key: 'HISTORY',
            label: 'Lịch sử nhập dữ liệu'
        }
    ];

    const columns: TableColumnsType<any> = [
        {
            title: 'STT',
            render: (_, __, index) => (pagination.curPage - 1) * pagination.limitPage + index + 1,
            width: 100
        },
        {
            title: 'Tên file',
            dataIndex: 'name',
            key: 'name',
            width: 400
        },
        {
            title: 'Tải file xuống',
            dataIndex: 'url',
            key: 'url',
            render: (url: string) => {
                return (
                    <div
                        css={css`
              display: flex;
              justify-content: center;
              align-items: center;

              .ant-btn {
                border: none;
              }
            `}
                    >
                        <Button icon={<DownloadOutlined />}
                            onClick={() => handleDownload(url)}
                        ></Button>
                    </div>
                );
            },
            width: 100
        },
        {
            title: 'Ngày nhập thực đơn',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 200,
            render: (record) => (record ? formatDateToHHmmDDMMYYYY(record) : '')
        },
        {
            title: 'Người nhập',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 200
        }
    ];

    const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
        }: PageChangeParams) => {
            dispatch(getHistory({
                offset: page,
                limit: pageSize
            }));
        },
        [dispatch]
    );

    const handleGoBack = useCallback(() => {
        push(PageURLs.ofComboFoodManagement());
    }, [push]);

    useEffect(() => {
        if (disableZScore) {
            form.resetFields(['zScore_60']);
        }
        if (disableBMI60_19) {
            form.resetFields(['bmi60Month19Age']);
        }
        if (disableBMIGe19) {
            form.resetFields(['bmiGe19Age']);
        }
    }, [form, disableZScore, disableBMI60_19, disableBMIGe19]);


    return (
        <div css={rootStyles}>
            <Row css={boxStyles} style={{ marginTop: '1rem' }} align='middle' gutter={16}>
                <Button
                    type='text'
                    icon={<ArrowLeftOutlined />}
                    onClick={handleGoBack}
                ></Button>
                <Typography.Title level={5} style={{ marginBottom: 0 }}>
                    Nhập thực đơn chi tiết theo danh sách
                </Typography.Title>
            </Row>

            <Tabs
                css={tabStyle}
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
            >
                {tabItems.map((item) => (
                    <Tabs.TabPane tab={item.label} key={item.key} />
                ))}
            </Tabs>

            {activeTab === 'INPUT' && (
                <>
                    <Form form={form} onFieldsChange={checkFormValid} onFinish={onFinish}>
                        <div css={boxStyles}>
                            <Row gutter={16} align='stretch'>
                                <Col span={12} css={filterItem}>
                                    <Form.Item
                                        name="age"
                                        css={customLabel}
                                        labelCol={{ span: 8 }}
                                        label={
                                            <span>
                                                Độ tuổi
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        rules={[{ required: true, message: 'Vui lòng chọn độ tuổi!' }]}
                                    >
                                        <Select
                                            placeholder='Chọn độ tuổi'
                                            allowClear
                                            options={listAgeOptions}
                                            onChange={handleAgeChange}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12} css={filterItem}>
                                    <Form.Item
                                        name="healthCondition"
                                        css={customLabel}
                                        label={
                                            <span>
                                                Thực đơn xây dựng cho tình trạng sức khỏe
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        rules={[{ required: true, message: 'Vui lòng chọn thực đơn xây dựng cho tình trạng sức khỏe!' }]}
                                    >
                                        <Select
                                            mode={isUnder19 ? undefined : 'multiple'}
                                            placeholder=''
                                            allowClear
                                            options={listNutritionHealthStatusOptions}
                                            onChange={(value) => setHealthCondition(value)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16} align='stretch'>
                                <Col span={8} css={filterItem}>
                                    <Form.Item
                                        name="zScore_60"
                                        css={customLabel}
                                        labelCol={{ span: 8 }}
                                        label={
                                            <span>
                                                Tình trạng dinh dưỡng theo Zscore (&lt;60 tháng tuổi)
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        rules={disableZScore ? [] : [{ required: true, message: 'Vui lòng chọn tình trạng dinh dưỡng!' }]}
                                    >
                                        <Select
                                            placeholder=''
                                            options={listNutritionStatusZScoreOptions}
                                            onChange={(value) => setZSCORE_60(value)}
                                            allowClear
                                            disabled={disableZScore}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8} css={filterItem}>
                                    <Form.Item
                                        name="bmi60Month19Age"
                                        css={customLabel}
                                        label={
                                            <span>
                                                Tình trạng dinh dưỡng theo BMI (60 tháng - 19 tuổi)
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        rules={disableBMI60_19 ? [] : [{ required: true, message: 'Vui lòng chọn tình trạng dinh dưỡng!' }]}
                                    >
                                        <Select
                                            placeholder=''
                                            options={listNutritionStatus60to19Options}
                                            onChange={(value) => setBMI60Month19Age(value)}
                                            allowClear
                                            disabled={disableBMI60_19}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8} css={filterItem} className='custom-form-input'>
                                    <Form.Item
                                        name="bmiGe19Age"
                                        css={customLabel}
                                        label={
                                            <span>
                                                Tình trạng dinh dưỡng theo BMI (&gt;19 tuổi)
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        rules={disableBMIGe19 ? [] : [{ required: true, message: 'Vui lòng chọn tình trạng dinh dưỡng!' }]}
                                    >
                                        <Select
                                            placeholder=''
                                            options={listNutritionStatusOptions}
                                            onChange={(value) => setBMIGe19Age(value)}
                                            allowClear
                                            disabled={disableBMIGe19}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify='center'>
                                <Button type='primary' icon={<UploadOutlined />}
                                    disabled={!healthCondition || (Array.isArray(healthCondition) && healthCondition.length === 0)}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Tải lên
                                    <input
                                        id="file-upload"
                                        type="file"
                                        style={{ display: 'none' }}
                                        accept="
                                            .xls,
                                            .xlsx,
                                            .xlsm,
                                            .xltx,
                                            .xltm,
                                            .xlsb,
                                            application/vnd.ms-excel,
                                            application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                                            application/vnd.ms-excel.sheet.macroEnabled.12,
                                            application/vnd.openxmlformats-officedocument.spreadsheetml.template,
                                            application/vnd.ms-excel.template.macroEnabled.12,
                                            application/vnd.ms-excel.sheet.binary.macroEnabled.12"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />
                                </Button>
                                <Button
                                    type='link'
                                    onClick={() => handleDownLoadTemplateComboFood()}
                                >
                                    Tải xuống template
                                </Button>
                            </Row>
                        </div>

                        <div css={boxStyles} style={{ marginTop: '1rem' }} >
                            {selectedFile && (
                                <div css={fileNameStyle}>
                                    <label>
                                        <PaperClipOutlined />
                                        {fileName}
                                    </label>
                                    <ButtonAnt
                                        onClick={() => handleCancel()}
                                        type='primary'
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </div>
                            )}
                            <div css={boxFooterStyles}>
                                <Button
                                    type='primary'
                                    // onClick={handleUpload}
                                    loading={loading}
                                    disabled={!isFormValid}
                                    htmlType='submit'
                                >Bắt đầu tải lên</Button>
                            </div>
                        </div>
                    </Form>
                </>
            )}
            {activeTab === 'HISTORY' && (
                <Table<any>
                    columns={columns}
                    dataSource={listHistory}
                    loading={loading}
                    pagination={{
                        showQuickJumper: false,
                        showSizeChanger: true,
                        current: pagination.curPage,
                        pageSize: pagination.limitPage,
                        total: pagination.totalRows,
                        onChange: (page, pageSize) => {
                            handlePageChange({ page, pageSize });
                        }
                    }}
                />
            )}
        </div>
    );
}

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));
  background-color: var(--gray-soft-color);

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .custom-form-input {
    align-items: center !important;
  }
`;

const fileNameStyle = css`
  padding: 1rem;
  background: var(--gray-soft-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  };
  display: flex;
  justify-content: space-between;
`
const boxFooterStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 50rem;
  padding-right: 50rem;
  .ant-btn {
    width: 25rem;
  };
  margin-top: 1rem;
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);

    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  .ant-form-item-control {
    display: flex !important;
    justify-content: center !important;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;

    &::after,
    &::before {
      display: none !important;
    }

    margin: 0 2rem 0 0 !important;
  }

  width: 100%;
  margin-bottom: 0;
`;